import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that adds a single curated upsell product to the cart in
 * one click, without leaving the cart page or drawer.
 *
 * Deliberately doesn't morph any section itself: it just dispatches
 * CartLinesUpdateEvent with the sections HTML returned by /cart/add.js.
 * `cart-items-component` (assets/component-cart-items.js) already listens for
 * that event on `document` and morphs itself with the returned HTML — the
 * same mechanism the regular product-form add-to-cart button relies on.
 *
 * @typedef {object} CartUpsellRefs
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} errorMessage
 * @extends {Component<CartUpsellRefs>}
 */
class CartUpsellComponent extends Component {
  requiredRefs = ['addButton', 'errorMessage'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  /**
   * Adds the curated upsell product's variant to the cart.
   * @param {MouseEvent} event
   */
  addUpsell = async (event) => {
    const { addButton, errorMessage } = this.refs;
    const variantId = this.dataset.variantId;
    const sectionId = this.dataset.sectionId;

    if (!variantId || addButton.disabled) return;

    if (this.#activeFetch) this.#activeFetch.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    addButton.disabled = true;
    errorMessage.classList.add('hidden');

    const deferredPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferredPromise.promise,
      })
    );

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionId || '',
        }),
      });

      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (data.status) {
        throw new Error(data.message || 'Add to cart failed');
      }

      // Fix R8 : même limite que cart-bump — add.js ne renvoie pas la devise.
      // Fix R9 : l'ajout a RÉUSSI ; si seul le refetch du cart échoue, ne pas afficher
      // un faux échec ni réactiver le bouton (l'article est déjà au panier).
      let fullCart;
      try {
        fullCart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      } catch (refetchError) {
        deferredPromise.reject(refetchError);
        return;
      }
      deferredPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart),
        detail: {
          items: data.items,
          sections: data.sections,
          source: 'cart-upsell-component',
          didError: false,
        },
      });
    } catch (error) {
      deferredPromise.reject(error);
      addButton.disabled = false;

      if (error instanceof Error && error.name !== 'AbortError') {
        errorMessage.classList.remove('hidden');

        this.dispatchEvent(
          new CartErrorEvent({
            error: error.message || 'Failed to add upsell product',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('cart-upsell-add:user-action', event);
    }
  };
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
